import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Brightness7, Brightness4, Visibility, VisibilityOff } from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import { ColorModeContext } from '../../theme';
import AuthContainerForm from '../../components/AuthContainerForm';
import { FirstLoginChangePasswordSchema } from '../../schemas/Schemas';
import useUserService from '../../api/services/userService';
import useAuthenticationService from '../../api/services/authenticationService';
import { getUserFromCookies } from '../../utils/Cookie-utils';
import { useAuth } from '../../hooks/useAuth';
import { getAccessToken } from '../../utils/session-utils';

const initialValues = {
  currentPassword: '',
  password: '',
  confirmPassword: '',
};

const passwordRequirements = [
  'At least 8 characters',
  'One uppercase letter (A-Z)',
  'One lowercase letter (a-z)',
  'One number (0-9)',
  'One special character (!@#$%...)',
];

const ChangePasswordPage = () => {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const { changePassword } = useUserService();
  const { fetchUserDetails } = useAuthenticationService();
  const { refreshUser } = useAuth();

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const user = getUserFromCookies();
  const userId = user?.data?.id;
  const isFirstLogin = user?.data?.loginForFirstTime === true;

  useEffect(() => {
    if (!getAccessToken()) {
      navigate('/', { replace: true });
      return;
    }
    if (!isFirstLogin) {
      navigate('/dashboard', { replace: true });
    }
  }, [isFirstLogin, navigate]);

  const handleSubmit = async (values, { setSubmitting }) => {
    const changePasswordDto = {
      currentPassword: values.currentPassword,
      password: values.password,
    };

    try {
      await changePassword(userId, changePasswordDto);
      await fetchUserDetails();
      if (refreshUser) {
        await refreshUser();
      }
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Failed to change password:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!getAccessToken() || !isFirstLogin) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        px: 2,
        py: { xs: 1.5, md: 2 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        overflow: 'hidden',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(180deg, #09111f 0%, #0f1c2f 100%)'
          : 'linear-gradient(180deg, #f4f8ff 0%, #eef4ff 100%)',
      }}
    >
      <Box display="flex" justifyContent="flex-end" alignItems="center" width="100%" maxWidth={1200} mx="auto">
        <IconButton
          onClick={colorMode.toggleColorMode}
          color="inherit"
          sx={{
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            borderRadius: '12px',
            px: 1.5,
          }}
        >
          {theme.palette.mode === 'dark' ? (
            <>
              <Typography variant="body2" mr={1}>DARK</Typography>
              <Brightness7 />
            </>
          ) : (
            <>
              <Typography variant="body2" mr={1}>LIGHT</Typography>
              <Brightness4 />
            </>
          )}
        </IconButton>
      </Box>

      <AuthContainerForm title="CHANGE PASSWORD">
        <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
          This is your first login. Please create a strong new password before continuing.
        </Typography>

        <Box
          sx={{
            width: '100%',
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
            Password requirements
          </Typography>
          <List dense disablePadding>
            {passwordRequirements.map((item) => (
              <ListItem key={item} disableGutters sx={{ py: 0 }}>
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Formik
          initialValues={initialValues}
          validationSchema={FirstLoginChangePasswordSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form style={{ width: '100%' }}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Field
                  as={TextField}
                  type={showCurrentPassword ? 'text' : 'password'}
                  label="Current Password"
                  name="currentPassword"
                  fullWidth
                  variant="outlined"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.currentPassword}
                  error={touched.currentPassword && !!errors.currentPassword}
                  helperText={touched.currentPassword && errors.currentPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowCurrentPassword((prev) => !prev)} edge="end">
                          {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Field
                  as={TextField}
                  type={showNewPassword ? 'text' : 'password'}
                  label="New Password"
                  name="password"
                  fullWidth
                  variant="outlined"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.password}
                  error={touched.password && !!errors.password}
                  helperText={touched.password && errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowNewPassword((prev) => !prev)} edge="end">
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Field
                  as={TextField}
                  type={showConfirmPassword ? 'text' : 'password'}
                  label="Confirm New Password"
                  name="confirmPassword"
                  fullWidth
                  variant="outlined"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.confirmPassword}
                  error={touched.confirmPassword && !!errors.confirmPassword}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end">
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={isSubmitting}
                  sx={{ py: 1.2, textTransform: 'none', fontWeight: 700 }}
                >
                  {isSubmitting ? 'Saving...' : 'Save New Password'}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </AuthContainerForm>
    </Box>
  );
};

export default ChangePasswordPage;
